import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Link, useNavigate } from 'react-router-dom';
import { useSocket } from 'use-socketio';
import RoomList from '../components/menu/RoomList';
import CreateRoomForm from '../components/menu/CreateRoomForm';
import WelcomePanel from '../components/menu/WelcomePanel';
import MenuModal from '../components/menu/MenuModal';
import RobotIcon from '../components/icons/RobotIcon';
import LanguagePicker from '../components/LanguagePicker';
import scrollbar from '../styles/scrollbar';
import { useT } from '../i18n';
import { hasSeenIntro, markIntroSeen } from '../firstVisit';

const ROBOT_COLORS = ['#d94f2b', '#2a63a8', '#e0a325', '#2f9e6e'];

const ADJECTIVES = ['swift', 'bold', 'iron', 'amber', 'lunar', 'sonic', 'steel', 'neon'];
const NOUNS = ['rover', 'nova', 'sigma', 'orbit', 'pulse', 'drift', 'spark', 'prism'];

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

function randomRoomId() {
  const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
  const num = Math.floor(Math.random() * 90 + 10);
  return `${adj}-${noun}-${num}`;
}

/** A board frame with a robot's ricochet path turning inside it. */
function HowToIcon() {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="square"
      style={{ flexShrink: 0, display: 'block' }}
    >
      <rect x="1.4" y="1.4" width="13.2" height="13.2" rx="1.6" />
      <path d="M4.2 11.4V6.2h4.4v3.2h3.2" strokeLinecap="butt" />
      <path d="M9.9 7.9l2.3 1.5-2.3 1.5z" fill="currentColor" stroke="none" />
      <circle cx="4.2" cy="11.4" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Menu — pressed-tin plates.
 *
 * The three ways into a game (create / browse / room code) live in ONE
 * recessed well directly under the nickname, so the card reads as: who you
 * are, then how you get in. Create is the only raised tomato plate — it is
 * the confident default for someone arriving with no code and no rooms up —
 * and Join demotes to a walnut plate beside the code field so it does not
 * compete with it. How to play sits outside the well as a quiet plate: it is
 * not a way into a game.
 */
const useStyles = createUseStyles({
  // The page owns the viewport and is the one scrolling region. `index.css`
  // pins `html { overflow: hidden }` so the board never rubber-bands, which
  // means anything past the fold here is *unreachable*, not merely off-screen:
  // the card runs ~710px against 667-711px of visible phone, and the language
  // row, last in it, was what fell off. Same trap as `HowToPlay`, same fix.
  page: {
    height: '100dvh',
    fallbacks: [{ height: '100vh' }],
    overflowY: 'auto',
    display: 'flex',
    padding: 'clamp(12px, 2vh, 24px) 16px',
    background: 'radial-gradient(ellipse at 50% 0%, #3d2010 0%, #1a0f08 62%)',
    ...scrollbar,
  },

  // `margin: auto` is the centring, not `alignItems`/`justifyContent` on the
  // page: those overflow a scroll container in both directions and strand the
  // top of a card taller than the scrollport, which is the half nobody can
  // scroll back to. Auto margins centre only the slack that exists.
  //
  // The vertical rhythm is fluid so scrolling stays the safety net rather than
  // the everyday experience: on a short phone the padding and gaps give back
  // ~70px, enough for the whole card, and both sit at their old values from
  // about 760px of viewport up, so nothing changes on a desktop.
  card: {
    margin: 'auto',
    width: '100%',
    maxWidth: '420px',
    boxSizing: 'border-box',
    background: '#2a1d13',
    borderRadius: '14px',
    border: '1px solid #6b4622',
    padding: 'clamp(18px, 3.2vh, 30px) 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: 'clamp(14px, 2.4vh, 22px)',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.12)',
      'inset 0 -3px 0 rgba(0,0,0,0.4)',
      '0 24px 60px rgba(0,0,0,0.6)',
    ].join(','),
  },

  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': { filter: 'brightness(1.15)' },
    '&:active': { filter: 'none' },
  },

  marks: { display: 'flex', gap: '10px' },

  mark: { width: '30px', height: '30px' },

  title: {
    margin: 0,
    fontSize: '30px',
    fontWeight: '700',
    color: '#f5e6c8',
    letterSpacing: '5px',
    textTransform: 'uppercase',
    textShadow: '0 2px 0 rgba(0,0,0,0.5)',
  },

  subtitle: {
    margin: 0,
    fontFamily: MONO,
    fontSize: '10.5px',
    color: '#c4a070',
    letterSpacing: '3px',
    textTransform: 'uppercase',
  },

  divider: {
    height: '1px',
    background: 'linear-gradient(to right, transparent, #6b4622 20%, #6b4622 80%, transparent)',
    boxShadow: '0 1px 0 rgba(255,255,255,0.06)',
  },

  section: { display: 'flex', flexDirection: 'column', gap: '8px' },

  label: {
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: '700',
    color: '#c4a070',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },

  input: {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    background: '#150d07',
    border: '1px solid #4a3020',
    borderRadius: '8px',
    color: '#f5e6c8',
    padding: '12px 14px',
    fontSize: '14px',
    outline: 'none',
    fontFamily: 'inherit',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
    transition: 'border-color 0.15s',
    '&:focus': { borderColor: '#8b4513' },
    '&::placeholder': { color: '#6a4a2a' },
  },

  // The one well every way in sits inside.
  wayIn: {
    background: '#1c130c',
    borderRadius: '11px',
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.55), inset 0 0 0 1px #402a1a',
  },

  createBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    minHeight: '58px',
    padding: '10px 14px',
    boxSizing: 'border-box',
    background: '#d94f2b',
    border: '1px solid #120c07',
    borderRadius: '9px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    color: '#fff3e6',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.3)',
      'inset 0 -3px 0 rgba(0,0,0,0.28)',
      '0 3px 0 #7d2a12',
    ].join(','),
    transition: 'background 0.12s, transform 0.06s, box-shadow 0.06s',
    '&:hover': { background: '#e35c36' },
    '&:active': {
      transform: 'translateY(3px)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), inset 0 2px 5px rgba(0,0,0,0.4)',
    },
    '&:focus-visible': { outline: '2px solid #f5e6c8', outlineOffset: '2px' },
  },

  createMark: {
    width: '26px',
    height: '26px',
    flexShrink: 0,
    borderRadius: '7px',
    background: 'rgba(0,0,0,0.22)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '17px',
    lineHeight: 1,
    fontWeight: 700,
  },

  createText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' },

  createTitle: {
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },

  createSub: {
    fontFamily: MONO,
    fontSize: '10px',
    letterSpacing: '0.5px',
    color: 'rgba(255,243,230,0.72)',
  },

  createChevron: { fontSize: '17px', lineHeight: 1, flexShrink: 0, color: 'rgba(255,243,230,0.7)' },

  orRow: { display: 'flex', alignItems: 'center', gap: '10px' },

  orLine: { flex: 1, height: '1px', background: '#402a1a', boxShadow: '0 1px 0 rgba(255,255,255,0.05)' },

  orText: {
    fontFamily: MONO,
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: '#8a6e4c',
  },

  browseBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    minHeight: '52px',
    padding: '10px 14px',
    boxSizing: 'border-box',
    background: '#3a2416',
    border: 'none',
    borderRadius: '9px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    color: '#e8d6ae',
    boxShadow: [
      'inset 0 0 0 1px #4a3020',
      'inset 0 1px 0 rgba(255,255,255,0.08)',
      'inset 0 -3px 0 rgba(0,0,0,0.35)',
    ].join(','),
    transition: 'background .12s, color .12s',
    '&:hover': { background: '#472c1a', color: '#fff3e6' },
    '&:focus-visible': { outline: '2px solid #e35c36', outlineOffset: '2px' },
  },

  browseMark: { fontSize: '15px', flexShrink: 0, lineHeight: 1, color: '#c4a070' },

  browseLabel: {
    flex: 1,
    minWidth: 0,
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },

  // The live count is the reason the lobby subscription runs on mount rather
  // than on modal open.
  browseCount: {
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    color: '#1a0f08',
    background: '#2f9e6e',
    borderRadius: '20px',
    padding: '3px 8px',
    flexShrink: 0,
  },

  browseCountEmpty: { background: '#4a3020', color: '#c4a070' },

  browseChevron: { fontSize: '17px', color: '#c4a070', lineHeight: 1, flexShrink: 0 },

  codeRow: { display: 'flex', gap: '8px' },

  codeInput: {
    flex: 1,
    minWidth: 0,
    boxSizing: 'border-box',
    background: '#150d07',
    border: '1px solid #4a3020',
    borderRadius: '8px',
    color: '#f5e6c8',
    padding: '0 13px',
    height: '46px',
    fontSize: '13px',
    outline: 'none',
    fontFamily: MONO,
    letterSpacing: '1px',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
    transition: 'border-color 0.15s',
    '&:focus': { borderColor: '#8b4513' },
    '&::placeholder': { color: '#6a4a2a' },
  },

  diceBtn: {
    width: '46px',
    height: '46px',
    flexShrink: 0,
    background: '#3a2718',
    border: '1px solid #6b4622',
    borderRadius: '8px',
    color: '#e8d6ae',
    cursor: 'pointer',
    fontSize: '17px',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -2px 0 rgba(0,0,0,0.35)',
    transition: 'background 0.15s, color 0.15s',
    '&:hover': { background: '#4a3220', color: '#fff3e6' },
    '&:active': { boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)' },
  },

  // Walnut, not tomato: Create is the primary in this well, and a Join that
  // shouted would pull the eye past it.
  joinBtn: {
    height: '46px',
    padding: '0 18px',
    flexShrink: 0,
    background: '#3a2718',
    border: '1px solid #6b4622',
    borderRadius: '8px',
    color: '#e8d6ae',
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -2px 0 rgba(0,0,0,0.35)',
    transition: 'background 0.15s, color 0.15s',
    '&:hover:not(:disabled)': { background: '#4a3220', color: '#fff3e6' },
    '&:active:not(:disabled)': { boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)' },
    '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
  },

  howTo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '9px',
    minHeight: '44px',
    padding: '0 14px',
    boxSizing: 'border-box',
    borderRadius: '9px',
    background: '#241811',
    color: '#a8875d',
    textDecoration: 'none',
    fontFamily: MONO,
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    boxShadow: 'inset 0 0 0 1px #402a1a, inset 0 1px 0 rgba(255,255,255,0.05)',
    transition: 'background .12s, color .12s',
    '&:hover': { background: '#31200f', color: '#f5e6c8' },
    '&:focus-visible': { outline: '2px solid #e35c36', outlineOffset: '2px' },
  },
  // Outside the ways-in well, beside How to play: choosing a language is not a
  // way into a game, and somebody who needs it needs it before they pick one.
  languageRow: {
    marginTop: '10px',
  },
});

function Menu() {
  const classes = useStyles();
  const t = useT();
  const navigate = useNavigate();
  const [gameId, setGameId] = useState('');
  const [nickname, setNickname] = useState(localStorage.getItem('nickname') ?? '');
  const { socket } = useSocket();
  const [rooms, setRooms] = useState([]);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // Seeded when the modal opens, so the suggested code is stable while it is
  // on screen rather than re-rolling on every keystroke elsewhere on the card.
  const [createSeed, setCreateSeed] = useState('');
  // Read once, on mount: whether the panel is up is this render's business,
  // and re-reading storage mid-visit would reopen it the moment it is answered.
  const [welcomeOpen, setWelcomeOpen] = useState(() => !hasSeenIntro());

  useSocket('ROOM_LIST', ({ rooms: list }) => setRooms(list ?? []));

  // The menu is the only screen in the lobby room, and it leaves on the way
  // out — JOIN_GAME also leaves it server-side, so a stale membership cannot
  // confuse withPlayer.
  useEffect(() => {
    socket.emit('LOBBY_SUBSCRIBE');
    return () => socket.emit('LOBBY_UNSUBSCRIBE');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const takenCodes = new Set(rooms.map(r => r.gameId));

  const openCreate = () => {
    setCreateSeed(gameId.trim() || randomRoomId());
    setCreateOpen(true);
  };

  const handleCreate = ({ gameId: id, create }) => {
    // No socket call: the policy rides to the server on the JOIN_GAME the Game
    // route emits, and is honoured only if that join builds the room.
    navigate(`/game/${id}`, { state: { create } });
  };

  const updateNickname = (value) => {
    setNickname(value);
    localStorage.setItem('nickname', value);
  };

  // Both answers are an answer, so both close the offer for good. Nobody is
  // held here: the card underneath stays live, and this is the menu route
  // only — an invited player arriving on /game/<code> never meets it.
  const showTutorial = () => {
    markIntroSeen();
    setWelcomeOpen(false);
    navigate('/how-to-play');
  };

  const skipTutorial = () => {
    markIntroSeen();
    setWelcomeOpen(false);
  };

  const handleJoin = () => {
    const id = gameId.trim();
    if (!id) return;
    navigate(`/game/${id}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleJoin();
  };

  const handleDice = () => {
    setGameId(randomRoomId());
  };

  const countClass = rooms.length
    ? classes.browseCount
    : `${classes.browseCount} ${classes.browseCountEmpty}`;

  return (
    <div className={classes.page}>
      <div className={classes.card}>

        <Link className={classes.header} to="/" title={t('a11y.backToMenu')}>
          <div className={classes.marks}>
            {ROBOT_COLORS.map(c => (
              <div key={c} className={classes.mark}>
                <RobotIcon color={c} />
              </div>
            ))}
          </div>
          <h1 className={classes.title}>Robotrons</h1>
          <p className={classes.subtitle}>{t('menu.subtitle')}</p>
        </Link>

        <div className={classes.divider} />

        <div className={classes.section}>
          <div className={classes.label}>{t('menu.nickname')}</div>
          <input
            className={classes.input}
            type="text"
            value={nickname}
            onChange={e => updateNickname(e.target.value)}
            placeholder={t('menu.nicknamePlaceholder')}
            spellCheck={false}
            autoComplete="off"
          />
        </div>

        <div className={classes.section} style={{ gap: '10px' }}>
          <div className={classes.label}>{t('menu.getIntoAGame')}</div>

          <div className={classes.wayIn}>
            <button type="button" className={classes.createBtn} onClick={openCreate}>
              <span className={classes.createMark} aria-hidden="true">+</span>
              <span className={classes.createText}>
                <span className={classes.createTitle}>{t('menu.create')}</span>
                <span className={classes.createSub}>{t('menu.createSub')}</span>
              </span>
              <span className={classes.createChevron} aria-hidden="true">›</span>
            </button>

            <div className={classes.orRow}>
              <div className={classes.orLine} />
              <span className={classes.orText}>{t('menu.orJoinOne')}</span>
              <div className={classes.orLine} />
            </div>

            <button
              type="button"
              className={classes.browseBtn}
              onClick={() => setBrowseOpen(true)}
            >
              <span className={classes.browseMark} aria-hidden="true">▤</span>
              <span className={classes.browseLabel}>{t('menu.browse')}</span>
              <span className={countClass}>
                {rooms.length > 0 ? t('menu.liveCount', { count: rooms.length }) : t('menu.none')}
              </span>
              <span className={classes.browseChevron} aria-hidden="true">›</span>
            </button>

            <div className={classes.codeRow}>
              <input
                className={classes.codeInput}
                type="text"
                value={gameId}
                onChange={e => setGameId(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('menu.roomCodePlaceholder')}
                aria-label={t('a11y.roomCode')}
                spellCheck={false}
                autoComplete="off"
              />
              <button className={classes.diceBtn} onClick={handleDice} title={t('a11y.randomRoom')}>
                ⚄
              </button>
              <button
                className={classes.joinBtn}
                onClick={handleJoin}
                disabled={!gameId.trim()}
              >
                {t('menu.join')}
              </button>
            </div>
          </div>
        </div>

        <Link className={classes.howTo} to="/how-to-play">
          <HowToIcon />
          {t('menu.howToPlay')}
        </Link>

        <div className={classes.languageRow}>
          <LanguagePicker />
        </div>

      </div>

      <WelcomePanel
        open={welcomeOpen}
        onShowMe={showTutorial}
        onSkip={skipTutorial}
      />

      <MenuModal
        open={browseOpen}
        title={t('menu.activeRooms')}
        onClose={() => setBrowseOpen(false)}
      >
        {/* Picking a room navigates away, so this never has to close itself. */}
        <RoomList rooms={rooms} onPick={id => navigate(`/game/${id}`)} />
      </MenuModal>

      <MenuModal
        open={createOpen}
        title={t('menu.createRoom')}
        onClose={() => setCreateOpen(false)}
      >
        <CreateRoomForm
          initialCode={createSeed}
          takenCodes={takenCodes}
          onCreate={handleCreate}
          onCancel={() => setCreateOpen(false)}
        />
      </MenuModal>
    </div>
  );
}

export default Menu;
